import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PlayCircleIcon } from "@heroicons/react/24/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { LessonI, ModuleI } from "../../../types";
import Carousel from "../../../componentLibrary/Carousel";
import Card from "../../../componentLibrary/Card";
import { useGetRecaps } from "../../../api/queries";
import LoadingCard from "../../../components/LoadingCard";
import { useDeleteRecap } from "../../../api/mutations";
import { useNotification } from "../../../hooks/useNotification";
import Tooltip from "../../../componentLibrary/Tooltip";
import Button from "../../../componentLibrary/Button";
import { ErrorDisplay } from "../../../utils/ErrorHandling";

interface InProgressCarouselProps {
  showHeading?: boolean;
}

export default function InProgressCarousel({
  showHeading = false,
}: InProgressCarouselProps) {
  const queryClient = useQueryClient();
  const { data: recaps, isError, isLoading, error } = useGetRecaps();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const deleteMut = useDeleteRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: () => {
      notify("error", "There was an error deleting the recap.");
    },
  });

  const handleCancel = (recapId: string) => {
    deleteMut.mutate(recapId);
  };

  if (isLoading) return <LoadingCard className="h-[186px]" />;
  if (isError || !recaps) {
    return <ErrorDisplay error={error} messageSource="api" />;
  }

  return (
    <>
      {(recaps?.length ?? 0) > 0 ? (
        <>
          <div
            className={`pb-2 flex flex-row justify-start items-center content-center gap-2 ${showHeading ? "" : "hidden"}`}
          >
            <h3>In Progress</h3>
            <Tooltip
              position="bottom"
              icon={<InformationCircleIcon className="w-6 h-6" />}
              component={
                <div className="flex flex-col space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <PlayCircleIcon className="w-4 h-4 text-white shrink-0" />
                      <span className="text-sm">Continue section</span>
                    </div>
                    <span className="text-sm text-gray-300 ml-6">
                      Continue from the last page you visited.
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-white text-sm">
                      Remove from in-progress
                    </span>
                    <span className="text-sm text-gray-300 ml-6">
                      Reset the section and remove it from this list.
                    </span>
                  </div>
                </div>
              }
            />
          </div>
          <Carousel showButtonsUp={true}>
            {(recaps ?? []).map((recap) => (
              <div key={recap._id} className="w-full max-w-[368px] p-1 pb-2">
                <Card
                  color="#CEEDF9"
                  className="!rounded-2xl !border-gray-200 !shadow-md"
                  footer={
                    <div className="flex flex-col items-center content-center space-y-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        roundedFull={true}
                        className="w-full"
                        onClick={() => {
                          navigate(
                            `/topics/${(recap.module as ModuleI)?.slug ?? (recap.module as ModuleI)?._id}/sections/${(recap.lesson as LessonI)?.slug ?? (recap.lesson as LessonI)?._id}`,
                            {
                              state: {
                                initialPage: Math.max(
                                  0,
                                  (recap.pages_visited?.length ?? 0) - 1,
                                ),
                              },
                            },
                          );
                        }}
                      >
                        <PlayCircleIcon className="w-4 h-4 mr-1" />
                        Continue
                      </Button>
                      <Button
                        variant="cautionary"
                        size="sm"
                        roundedFull={true}
                        className="w-full"
                        onClick={() => handleCancel(recap._id)}
                      >
                        Remove from in-progress
                      </Button>
                    </div>
                  }
                >
                  <div className="min-h-20">
                    <div className="flex flex-row justify-start gap-3">
                      <img
                        className="w-14 h-14"
                        src={(recap.lesson as LessonI)?.icon.src}
                        alt={(recap.lesson as LessonI)?.icon.alt}
                      />
                      <div className="flex flex-col justify-center">
                        <span className="text-sm font-semibold whitespace-normal m-0">
                          {(recap.module as ModuleI)?.title}
                        </span>
                        <span className="text-md font-semibold text-black whitespace-normal m-0">
                          {(recap.lesson as LessonI)?.title}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </Carousel>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="py-4 text-center">
            <p className="text-sm text-gray-400 py-2">
              No sections in progress. Explore topics to get started.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
